import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, Pencil } from "lucide-react";

const VISA_STYLE = {
  not_submitted: "bg-slate-100 text-slate-600 border-slate-300",
  document_incomplete: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-indigo-50 text-indigo-700 border-indigo-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  issued: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function Customers() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editCust, setEditCust] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", passport: "", gender: "male" });

  const load = useCallback(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => toast.error("Failed"));
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Jemaah added");
      setOpen(false);
      setForm({ name: "", phone: "", passport: "", gender: "male" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/customers/${editCust.customer_id}`, {
        visa_status: editCust.visa_status, passport: editCust.passport,
        passport_expiry: editCust.passport_expiry, mykad: editCust.mykad, phone: editCust.phone,
      });
      toast.success("Jemaah updated");
      setEditCust(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const filtered = useMemo(() => (customers || []).filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.passport || "").toLowerCase().includes(search.toLowerCase())
  ), [customers, search]);

  if (!customers) return <div className="flex justify-center h-64 items-center" data-testid="customers-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.customers")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{customers.length} lifetime customer records</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search")} className="pl-9 w-52 rounded-full bg-white" />
          </div>
          <Button data-testid="open-portal-button" variant="outline" onClick={() => navigate("/app/portal")}
            className="rounded-full border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">
            <Eye className="w-4 h-4 mr-1.5" />{t("app.portal")}
          </Button>
          <Button data-testid="add-customer-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
            <Plus className="w-4 h-4 mr-1.5" />{t("common.add")}
          </Button>
        </div>
      </div>

      <div className="pro-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-sand-border text-left bg-[#F8F6F2]/80">
              {[t("common.name"), t("common.phone"), "MyKad", "Passport", "Expiry", "Visa Status", "Portal"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.customer_id} data-testid={`customer-row-${c.customer_id}`} className="border-b border-sand-border/60 hover:bg-emerald-50/60 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-brand-950">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.gender || "—"}</div>
                </td>
                <td className="px-4 py-3 text-xs">{c.phone}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.mykad || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.passport || "—"}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(c.passport_expiry)}</td>
                <td className="px-4 py-3">
                  <span data-testid={`visa-status-${c.customer_id}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${VISA_STYLE[c.visa_status] || VISA_STYLE.not_submitted}`}>
                    {(c.visa_status || "not_submitted").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`view-portal-${c.customer_id}`}
                      onClick={() => navigate(`/app/portal?customer_id=${c.customer_id}`)}
                      className="h-7 rounded-full text-xs border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">
                      <Eye className="w-3.5 h-3.5 mr-1" />View
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`edit-customer-${c.customer_id}`}
                      onClick={() => setEditCust({ ...c })} className="h-7 px-2 text-slate-400 hover:text-emerald-800">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm">No jemaah found</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-customer-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{t("common.add")} Jemaah</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="customer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("common.phone")}</Label>
                <Input data-testid="customer-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="customer-gender-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Passport</Label>
                <Input data-testid="customer-passport-input" value={form.passport} onChange={(e) => setForm({ ...form, passport: e.target.value })} />
              </div>
            </div>
            <Button data-testid="customer-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCust} onOpenChange={() => setEditCust(null)}>
        <DialogContent data-testid="edit-customer-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">Edit Jemaah — {editCust?.name}</DialogTitle></DialogHeader>
          {editCust && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label>Visa Status</Label>
                <Select value={editCust.visa_status || "not_submitted"} onValueChange={(v) => setEditCust({ ...editCust, visa_status: v })}>
                  <SelectTrigger data-testid="edit-visa-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["not_submitted", "document_incomplete", "submitted", "processing", "approved", "issued", "rejected"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Passport</Label>
                  <Input data-testid="edit-passport-input" value={editCust.passport || ""} onChange={(e) => setEditCust({ ...editCust, passport: e.target.value })} />
                </div>
                <div>
                  <Label>Passport Expiry</Label>
                  <Input data-testid="edit-passport-expiry-input" type="date" value={editCust.passport_expiry || ""} onChange={(e) => setEditCust({ ...editCust, passport_expiry: e.target.value })} />
                </div>
                <div>
                  <Label>MyKad</Label>
                  <Input data-testid="edit-mykad-input" value={editCust.mykad || ""} onChange={(e) => setEditCust({ ...editCust, mykad: e.target.value })} />
                </div>
                <div>
                  <Label>{t("common.phone")}</Label>
                  <Input data-testid="edit-phone-input" value={editCust.phone || ""} onChange={(e) => setEditCust({ ...editCust, phone: e.target.value })} />
                </div>
              </div>
              <Button data-testid="edit-customer-submit" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">{t("common.save")}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
